// src/pages/PaySuccess.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../styles/payment-design.css";

type PaymentStatus = {
  id: number;
  order_id: string;
  title: string;
  amount: number;
  total_amount?: number | string | null;
  status: string;
  method: string | null;
  category?: string | null;
  meta?: any;
};

function buildRegistryTarget(meta: any) {
  const m = meta && typeof meta === "object" ? meta : {};

  const categorySlug =
    (m.category_slug ||
      m.categorySlug ||
      m.category ||
      m.contract_type ||
      "rent") + "";

  const sessionKey =
    (m.session_key ||
      m.sessionKey ||
      m.upload_session ||
      m.uploadSession ||
      "") + "";

  const qs = new URLSearchParams();
  qs.set("category", String(categorySlug).toLowerCase());
  if (sessionKey) qs.set("session_key", sessionKey);
  qs.set("registry", "paid");

  return `/contracts/new/transfer?${qs.toString()}`;
}

function paymentOutcome(payment: PaymentStatus | null): "success" | "failure" | "unconfirmed" {
  if (payment?.status === "complete" || payment?.status === "paid") return "success";
  if (["failed", "rejected", "canceled", "cancelled"].includes(payment?.status || "")) return "failure";
  return "unconfirmed";
}

function paymentAmountSummary(payment: PaymentStatus | null) {
  const total = payment?.total_amount;
  const hasTotal = total !== null && total !== undefined && total !== "" && Number.isFinite(Number(total));
  const rawAmount = hasTotal ? total : payment?.amount;
  const amount = rawAmount !== null && rawAmount !== undefined && rawAmount !== "" ? Number(rawAmount) : NaN;
  return { label: hasTotal ? "총 결제금액" : "요청 원금", amount: Number.isFinite(amount) ? amount : null };
}

export default function PaySuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = useMemo(() => searchParams.get("orderId") || searchParams.get("orderID") || "", [searchParams]);
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState<PaymentStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!orderId) {
      setError("주문 정보를 확인할 수 없습니다. 이용목록을 확인해 주세요.");
      setPayment(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setPayment(null);
    setError(null);
    (async () => {
      try {
        const res = await fetch("/api/payments/status/" + encodeURIComponent(orderId), { cache: "no-store", signal: controller.signal });
        const json = await res.json().catch(() => null);
        if (!res.ok || !json?.ok || !json.payment || typeof json.payment !== "object") {
          throw new Error("결제 상태를 불러오지 못했습니다. 이용목록을 확인해 주세요.");
        }
        if (!controller.signal.aborted) setPayment(json.payment as PaymentStatus);
      } catch {
        if (!controller.signal.aborted) setError("결제 상태를 불러오지 못했습니다. 이용목록을 확인해 주세요.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [orderId, retry]);

  const outcome = paymentOutcome(payment);
  const meta = payment?.meta || {};
  const isRegistry = (meta.kind || meta.payment_kind || null) === "registry";
  const successTarget = isRegistry ? buildRegistryTarget(meta) : "/pay";
  const amountSummary = paymentAmountSummary(payment);

  useEffect(() => {
    if (loading || !orderId || error || !payment || outcome !== "success") return;
    const timer = window.setTimeout(() => navigate(successTarget, { replace: true }), 1400);
    return () => window.clearTimeout(timer);
  }, [loading, payment, navigate, orderId, error, outcome, successTarget]);

  const heading = loading ? "결제 상태 확인 중" : error ? "결제 확인이 필요해요" : outcome === "success" ? "결제가 확인되었어요" : outcome === "failure" ? "결제가 완료되지 않았어요" : "결제 확인이 필요해요";
  const description = loading ? "잠시만 기다려 주세요." : error || (outcome === "success" ? "잠시 후 다음 화면으로 이동합니다." : outcome === "failure" ? "이용목록에서 처리 상태를 확인해 주세요." : "완료 여부가 아직 확인되지 않았습니다.");

  return <div className="jp-payment-result jp-payment-result-fail">
    <section className="jp-payment-result-card" aria-busy={loading}>
      <span className="jp-payment-eyebrow">결제 결과</span>
      <h1>{heading}</h1>
      <p role={error ? "alert" : "status"}>{description}</p>
      {!loading && payment && <dl className="jp-payment-result-summary">
        <div><dt>주문번호</dt><dd>{payment.order_id || orderId}</dd></div>
        <div><dt>{amountSummary.label}</dt><dd>{amountSummary.amount === null ? "확인 필요" : amountSummary.amount.toLocaleString("ko-KR") + "원"}</dd></div>
        <div><dt>결제명</dt><dd>{payment.title || "결제 건"}</dd></div>
      </dl>}
      <div className="jp-payment-result-actions">
        <button type="button" className="jp-payment-result-primary" onClick={() => navigate(outcome === "success" ? successTarget : "/pay", { replace: true })}>{outcome === "success" && isRegistry ? "신청 계속하기" : "이용목록 확인"}</button>
        {!loading && outcome !== "success" && orderId && <button type="button" className="jp-payment-result-secondary" onClick={() => setRetry(value => value + 1)}>상태 다시 확인</button>}
      </div>
    </section>
  </div>;
}
